import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Star, ShieldCheck, Truck, RotateCcw, Zap } from "lucide-react";
import { fetchProduct, fetchReviews, resolveMediaUrl } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { useSeo } from "../lib/useSeo";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WishlistButton from "../components/WishlistButton";
import VerifiedReviews from "../components/VerifiedReviews";
import StockAlertForm from "../components/StockAlertForm";
import ProductRecommendations from "../components/ProductRecommendations";
import LimitedEditionBanner from "../components/LimitedEditionBanner";
import { toast } from "sonner";

const Product = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [volume, setVolume] = useState("100ml");
  const { addItem } = useCart();
  const { formatPrice, currency } = useCurrency();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProduct(slug).then(setProduct).catch(() => navigate("/"));
    fetchReviews().then((r) => setReviews(r.slice(0, 3)));
  }, [slug, navigate]);

  // SEO: dynamic per-product meta + JSON-LD Product schema (Google Merchant Center compatible)
  const productImages = (product?.image_gallery && product.image_gallery.length > 0)
    ? product.image_gallery
    : (product?.image_main ? [product.image_main] : []);
  const productPrice = product?.price_100ml ?? product?.price_50ml ?? 0;
  const avgRating = reviews.length > 0
    ? (reviews.reduce((s, r) => s + (r.rating || 5), 0) / reviews.length).toFixed(1)
    : null;

  // Build a rich, unique meta description with notes for better SEO indexing
  const notesText = product ? [
    product.top_notes?.length ? `Tête : ${product.top_notes.join(", ")}` : "",
    product.heart_notes?.length ? `Cœur : ${product.heart_notes.join(", ")}` : "",
    product.base_notes?.length ? `Fond : ${product.base_notes.join(", ")}` : "",
  ].filter(Boolean).join(" · ") : "";
  const seoDescription = product ? (
    `${product.name} — ${product.tagline || ""}. ${(product.description || "").slice(0, 160)}${notesText ? ". " + notesText : ""}`
  ).slice(0, 300).trim() : "";

  useSeo(product ? {
    title: `${product.name} — ${product.brand || "MAISON DOUBA"} · Eau de Parfum ${product.category || ""} 100 ml`.trim(),
    description: seoDescription,
    canonical: `https://wwwmaisondouba.com/parfum/${product.slug}`,
    image: productImages[0],
    jsonLd: {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": product.name,
      "description": product.description || `${product.name} — ${product.brand || "MAISON DOUBA"}`,
      "image": productImages,
      "brand": { "@type": "Brand", "name": product.brand || "MAISON DOUBA" },
      "sku": product.slug,
      "mpn": product.slug,
      "category": product.product_type === "watch" ? "Apparel & Accessories > Jewelry > Watches"
                : product.product_type === "sunglasses" ? "Apparel & Accessories > Clothing Accessories > Sunglasses"
                : product.product_type === "bag" ? "Apparel & Accessories > Handbags, Wallets & Cases > Handbags"
                : "Health & Beauty > Personal Care > Cosmetics > Perfume & Cologne",
      "offers": {
        "@type": "Offer",
        "url": `https://wwwmaisondouba.com/parfum/${product.slug}`,
        "priceCurrency": "EUR",
        "price": Number(productPrice).toFixed(2),
        "availability": (product.stock ?? 1) > 0
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
        "itemCondition": "https://schema.org/NewCondition",
        "priceValidUntil": new Date(Date.now() + 365 * 24 * 3600 * 1000).toISOString().slice(0, 10),
        "shippingDetails": {
          "@type": "OfferShippingDetails",
          "shippingRate": { "@type": "MonetaryAmount", "value": "0", "currency": "EUR" },
          "shippingDestination": { "@type": "DefinedRegion", "addressCountry": "FR" },
          "deliveryTime": {
            "@type": "ShippingDeliveryTime",
            "handlingTime": { "@type": "QuantitativeValue", "minValue": 1, "maxValue": 2, "unitCode": "d" },
            "transitTime": { "@type": "QuantitativeValue", "minValue": 2, "maxValue": 3, "unitCode": "d" }
          }
        },
        "hasMerchantReturnPolicy": {
          "@type": "MerchantReturnPolicy",
          "applicableCountry": "FR",
          "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
          "merchantReturnDays": 14,
          "returnMethod": "https://schema.org/ReturnByMail",
          "returnFees": "https://schema.org/FreeReturn"
        },
        "seller": { "@type": "Organization", "name": "MAISON DOUBA", "url": "https://wwwmaisondouba.com" }
      },
      ...(avgRating && {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": avgRating,
          "reviewCount": reviews.length,
          "bestRating": "5",
          "worstRating": "1"
        }
      })
    }
  } : {});

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="label-caps text-white/50">Chargement…</p>
      </div>
    );
  }

  const basePrice = volume === "100ml" ? product.price_100ml : product.price_50ml;
  const isOnSale = volume === "100ml"
    && typeof product.sale_price === "number"
    && product.sale_price > 0
    && product.sale_price < product.price_100ml;
  const price = isOnSale ? product.sale_price : basePrice;
  const discountPct = isOnSale
    ? Math.round(((product.price_100ml - product.sale_price) / product.price_100ml) * 100)
    : 0;

  const handleAdd = () => {
    addItem(product, volume);
    toast.success(`${product.name} ajouté à votre écrin`);
  };

  const handleExpressBuy = () => {
    // Add without opening drawer, then jump straight to checkout.
    addItem(product, volume, false);
    navigate("/checkout");
  };

  return (
    <div data-testid={`product-page-${slug}`} className="bg-[#050505]">
      <Navbar />

      <div className="pt-32 pb-24 max-w-[1600px] mx-auto px-6 md:px-12">
        <Link to="/" data-testid="back-to-home" className="label-caps text-white/50 hover:text-[color:var(--gold)] inline-flex items-center gap-3 mb-12">
          <ArrowLeft size={14} /> Retour
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20">
          {/* Images sticky */}
          <div className="lg:sticky lg:top-28 self-start">
            <div className="aspect-[3/4] bg-[#0A0A0A] overflow-hidden relative">
              <img src={resolveMediaUrl(product.image_main)} alt={product.name} className="w-full h-full object-cover" />
              {product.badge && (
                <span className="absolute top-6 left-6 label-caps text-black bg-[color:var(--gold)] px-4 py-2 text-[10px]">{product.badge}</span>
              )}
            </div>
            {product.image_gallery.length > 1 && (
              <div className="grid grid-cols-3 gap-3 mt-3">
                {product.image_gallery.slice(0, 3).map((img, i) => (
                  <div key={img || `gallery-${i}`} className="aspect-square bg-[#0A0A0A] overflow-hidden">
                    <img src={resolveMediaUrl(img)} alt="" className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity cursor-pointer" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <p className="label-caps text-[color:var(--gold)] mb-6">{product.brand ? `${product.brand} · ${product.category}` : `${product.category} · Eau de Parfum`}</p>
            <h1 data-testid="product-name" className="font-serif text-5xl md:text-6xl text-white leading-tight mb-4">{product.name}</h1>
            <p className="text-white/60 italic text-lg mb-8">{product.tagline}</p>

            <LimitedEditionBanner untilIso={product.limited_edition_until} stock={product.stock} />

            <div className="flex items-center gap-4 mb-10">
              <div className="flex gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} className={i < Math.round(product.rating) ? "fill-[color:var(--gold)] text-[color:var(--gold)]" : "text-white/20"} />
                ))}
              </div>
              <span className="label-caps text-white/50 text-[10px]">{product.rating} · {product.reviews_count} avis</span>
            </div>

            <p className="text-white/60 font-light leading-relaxed mb-12">{product.description}</p>

            {/* Volume selector (parfums uniquement) */}
            {product.product_type !== "watch" && (
              <div className="mb-8">
                <p className="label-caps text-white/50 mb-4 text-[10px]">Contenance</p>
                <div className="flex gap-3 flex-wrap">
                  {["50ml", "100ml"]
                    .filter((v) => v === "100ml" || (product.price_50ml && product.price_50ml > 0))
                    .map((v) => (
                      <button
                        key={v}
                        data-testid={`volume-${v}`}
                        onClick={() => setVolume(v)}
                        className={`px-6 py-3 label-caps text-xs border transition-all ${
                          volume === v
                            ? "border-[color:var(--gold)] text-[color:var(--gold)] bg-[color:var(--gold)]/5"
                            : "border-white/15 text-white/70 hover:border-white/40"
                        }`}
                      >
                        {v} — {formatPrice(v === "100ml" ? product.price_100ml : product.price_50ml)}
                      </button>
                    ))}
                </div>
              </div>
            )}

            <div className="flex items-baseline gap-4 mb-10 flex-wrap">
              <p data-testid="product-price" className={`font-serif text-4xl ${isOnSale ? "text-red-400" : "text-[color:var(--gold)]"}`}>
                {formatPrice(price)}
              </p>
              {isOnSale && (
                <>
                  <p className="font-serif text-xl text-white/40 line-through">{formatPrice(product.price_100ml)}</p>
                  <span data-testid="product-sale-badge" className="label-caps text-white bg-red-600 px-3 py-1.5 text-[10px] tracking-[0.25em]">
                    Solde −{discountPct}%
                  </span>
                </>
              )}
              <p className="text-white/40 text-sm w-full sm:w-auto">TVA incluse · Livraison offerte{currency !== "EUR" && " · facturé en EUR"}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              {product.stock === 0 ? (
                <div className="flex-1 flex items-center justify-center py-3 border border-white/20 text-white/50 text-sm label-caps tracking-[0.25em]">
                  Momentanément indisponible
                </div>
              ) : (
                <button data-testid="add-to-cart-btn" onClick={handleAdd} className="btn-gold flex-1">Ajouter à l'écrin <ArrowRight size={14} /></button>
              )}
              <div className="flex items-center justify-center border border-white/15 hover:border-[color:var(--gold)] px-5 transition-colors">
                <WishlistButton productId={product.id} size={20} />
              </div>
            </div>
            {product.stock > 0 && (
              <button
                data-testid="express-buy-btn"
                onClick={handleExpressBuy}
                className="w-full mb-12 py-3.5 border border-[color:var(--gold)]/40 bg-[color:var(--gold)]/5 hover:bg-[color:var(--gold)]/10 text-[color:var(--gold)] label-caps text-[11px] tracking-[0.3em] flex items-center justify-center gap-2.5 transition-colors group"
              >
                <Zap size={14} strokeWidth={1.5} className="group-hover:animate-pulse" />
                Achat express — paiement en 1 clic
                <ArrowRight size={14} />
              </button>
            )}

            {product.stock === 0 && (
              <StockAlertForm productId={product.id} productName={product.name} />
            )}

            {/* Trust mini */}
            <div className="grid grid-cols-3 gap-4 py-8 border-t border-b border-white/10 mb-12">
              <div className="flex items-center gap-3">
                <ShieldCheck size={18} className="text-[color:var(--gold)]" strokeWidth={1.2} />
                <span className="label-caps text-white/60 text-[9px]">Paiement sécurisé</span>
              </div>
              <div className="flex items-center gap-3">
                <Truck size={18} className="text-[color:var(--gold)]" strokeWidth={1.2} />
                <span className="label-caps text-white/60 text-[9px]">Livraison 48–72h</span>
              </div>
              <div className="flex items-center gap-3">
                <RotateCcw size={18} className="text-[color:var(--gold)]" strokeWidth={1.2} />
                <span className="label-caps text-white/60 text-[9px]">Retours 30j</span>
              </div>
            </div>

            {/* Notes olfactives (parfum) OU Spécifications (montre) */}
            {product.product_type === "watch" ? (
              <div className="mb-12">
                <p className="label-caps text-[color:var(--gold)] mb-6">Caractéristiques</p>
                <div className="space-y-0">
                  {Object.entries(product.specifications || {}).map(([k, v]) => (
                    <div key={k} className="grid grid-cols-3 gap-4 items-start border-b border-white/5 py-5">
                      <p className="label-caps text-white/45 text-[10px]">{k}</p>
                      <p className="col-span-2 text-white/80 font-light">{v}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mb-12">
                <p className="label-caps text-[color:var(--gold)] mb-6">Pyramide olfactive</p>
                <div className="space-y-6">
                  {[
                    { label: "Notes de tête", items: product.top_notes },
                    { label: "Notes de cœur", items: product.heart_notes },
                    { label: "Notes de fond", items: product.base_notes },
                  ].map((row) => (
                    <div key={row.label} className="grid grid-cols-3 gap-4 items-start border-b border-white/5 pb-6">
                      <p className="label-caps text-white/45 text-[10px]">{row.label}</p>
                      <p className="col-span-2 text-white/80 font-light">{(row.items || []).join(" · ")}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Details additionnels (parfum uniquement) */}
            {product.product_type !== "watch" && (
              <div className="space-y-4 mb-12">
                {product.intensity && (
                  <div className="grid grid-cols-3 gap-4 py-4 border-b border-white/5">
                    <p className="label-caps text-white/45 text-[10px]">Intensité</p>
                    <p className="col-span-2 text-white/80 font-light">{product.intensity}</p>
                  </div>
                )}
                {product.longevity && (
                  <div className="grid grid-cols-3 gap-4 py-4 border-b border-white/5">
                    <p className="label-caps text-white/45 text-[10px]">Tenue</p>
                    <p className="col-span-2 text-white/80 font-light">{product.longevity}</p>
                  </div>
                )}
                {product.usage_tip && (
                  <div className="grid grid-cols-3 gap-4 py-4 border-b border-white/5">
                    <p className="label-caps text-white/45 text-[10px]">Conseil</p>
                    <p className="col-span-2 text-white/80 font-light">{product.usage_tip}</p>
                  </div>
                )}
                {product.ingredients && (
                  <div className="grid grid-cols-3 gap-4 py-4 border-b border-white/5">
                    <p className="label-caps text-white/45 text-[10px]">Ingrédients</p>
                    <p className="col-span-2 text-white/60 font-light text-sm">{product.ingredients}</p>
                  </div>
                )}
              </div>
            )}

            {/* Verified reviews (achat vérifié) */}
            <VerifiedReviews productId={product.id} />

            {/* Reviews snippet — testimonials (fallback while no verified reviews) */}
            {reviews.length > 0 && (
              <div className="mt-16">
                <p className="label-caps text-[color:var(--gold)] mb-6">Témoignages</p>
                <div className="space-y-6">
                  {reviews.map((r) => (
                    <div key={r.id} className="border-t border-white/10 pt-6">
                      <div className="flex gap-1 mb-3">
                        {Array.from({ length: r.rating }).map((_, k) => (
                          <Star key={k} size={12} className="fill-[color:var(--gold)] text-[color:var(--gold)]" />
                        ))}
                      </div>
                      <p className="font-serif text-lg text-white mb-2">« {r.title} »</p>
                      <p className="text-white/60 font-light text-sm">{r.body}</p>
                      <p className="label-caps text-white/40 text-[9px] mt-3">{r.author}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AI-style recommendations (in-stock, based on olfactory notes overlap) */}
      <ProductRecommendations slug={slug} />

      <Footer />
    </div>
  );
};

export default Product;
